import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Toggle, Loading, Select } from '../components/ui';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface AnnouncementContent {
  enabled: boolean;
  badge_text: string;
  eyebrow_text_en: string;
  eyebrow_text_ro: string;
  title_en: string;
  title_ro: string;
  highlight_text: string;
  description_en: string;
  description_ro: string;
  primary_button_text: string;
  primary_button_url: string;
  primary_button_type: string;
  secondary_button_text: string;
  secondary_button_url: string;
  secondary_button_type: string;
}

const buttonTypes = [
  { value: 'youtube', label: 'YouTube (Red)' },
  { value: 'spotify', label: 'Spotify (Green)' },
  { value: 'default', label: 'Default (Blue)' },
];

const AnnouncementPage: React.FC = () => {
  const { data, loading, fetch, update } = useApi<AnnouncementContent>('/api/content/announcement');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<AnnouncementContent>({
    enabled: false,
    badge_text: 'LIVE',
    eyebrow_text_en: '', eyebrow_text_ro: '',
    title_en: '', title_ro: '',
    highlight_text: '',
    description_en: '', description_ro: '',
    primary_button_text: '', primary_button_url: '', primary_button_type: 'youtube',
    secondary_button_text: '', secondary_button_url: '', secondary_button_type: 'spotify',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        enabled: data.enabled || false,
        badge_text: data.badge_text || 'LIVE',
        eyebrow_text_en: data.eyebrow_text_en || '',
        eyebrow_text_ro: data.eyebrow_text_ro || '',
        title_en: data.title_en || '',
        title_ro: data.title_ro || '',
        highlight_text: data.highlight_text || '',
        description_en: data.description_en || '',
        description_ro: data.description_ro || '',
        primary_button_text: data.primary_button_text || '',
        primary_button_url: data.primary_button_url || '',
        primary_button_type: data.primary_button_type || 'youtube',
        secondary_button_text: data.secondary_button_text || '',
        secondary_button_url: data.secondary_button_url || '',
        secondary_button_type: data.secondary_button_type || 'spotify',
      });
    }
  }, [data]);

  const handleChange = (field: keyof AnnouncementContent) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('Announcement saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Announcement Modal"
        subtitle="Configure the popup announcement that appears on page load"
        actions={<Button onClick={handleSave} loading={saving}>Save Changes</Button>}
      />

      <div className="admin-content">
        <Card title="Enable/Disable">
          <Toggle
            checked={form.enabled}
            onChange={checked => setForm(f => ({ ...f, enabled: checked }))}
            label="Show announcement popup to visitors"
          />
          <p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>
            When enabled, the announcement modal will appear when visitors first load the page.
          </p>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card title="Badge & Eyebrow">
            <div className="admin-form">
              <Input
                label="Badge Text"
                value={form.badge_text}
                onChange={handleChange('badge_text')}
                placeholder="LIVE"
                hint="Short text shown in the badge (e.g., LIVE, NEW, HOT)"
              />
              <LanguageTabs currentLang={lang} onChange={setLang} />
              {lang === 'en' ? (
                <Input label="Eyebrow Text (EN)" value={form.eyebrow_text_en} onChange={handleChange('eyebrow_text_en')} placeholder="New Release" />
              ) : (
                <Input label="Eyebrow Text (RO)" value={form.eyebrow_text_ro} onChange={handleChange('eyebrow_text_ro')} placeholder="Lansare nouă" />
              )}
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Main Content">
            <LanguageTabs currentLang={lang} onChange={setLang} />
            <div className="admin-form">
              {lang === 'en' ? (
                <>
                  <Input label="Title (EN)" value={form.title_en} onChange={handleChange('title_en')} placeholder="STREAM" />
                  <Input label="Description (EN)" value={form.description_en} onChange={handleChange('description_en')} placeholder="My new album drops at midnight..." />
                </>
              ) : (
                <>
                  <Input label="Title (RO)" value={form.title_ro} onChange={handleChange('title_ro')} placeholder="ASCULTĂ" />
                  <Input label="Description (RO)" value={form.description_ro} onChange={handleChange('description_ro')} placeholder="Noul meu album apare la miezul nopții..." />
                </>
              )}
              <Input
                label="Highlight Text (shown in bold)"
                value={form.highlight_text}
                onChange={handleChange('highlight_text')}
                placeholder="METAMORFOZA"
                hint="This text appears bold after the title"
              />
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Primary Button">
            <div className="admin-form">
              <Input label="Button Text" value={form.primary_button_text} onChange={handleChange('primary_button_text')} placeholder="Watch on YouTube" />
              <Input label="Button URL" value={form.primary_button_url} onChange={handleChange('primary_button_url')} placeholder="https://youtube.com/..." />
              <Select label="Button Style" options={buttonTypes} value={form.primary_button_type} onChange={handleChange('primary_button_type')} />
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Secondary Button">
            <div className="admin-form">
              <Input label="Button Text" value={form.secondary_button_text} onChange={handleChange('secondary_button_text')} placeholder="Listen on Spotify" />
              <Input label="Button URL" value={form.secondary_button_url} onChange={handleChange('secondary_button_url')} placeholder="https://open.spotify.com/..." />
              <Select label="Button Style" options={buttonTypes} value={form.secondary_button_type} onChange={handleChange('secondary_button_type')} />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AnnouncementPage;
